import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {AnyImageOpHalData, CtorArgs, ImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {RegionLockedHalImageRef} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/region-locked-hal-image-ref"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {MipLevelRegion, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {Definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/blit/definition"
import {
    determineBackgroundImageMipLevelRegion,
    determineForegroundImageMipLevelRegion,
    determineTargetImageRegion,
    getRegionsToDraw,
} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/blit/helpers"
import {HalImage} from "@cm/hal"
import {HalPainterImageCompositorRef} from "@cm/hal/hal-ref"
import {Box2} from "@cm/math"
import {getHalAddressMode} from "@cm/webgl2"
import {UPDATE_COMPLEXITY_MID} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

type Definition = typeof Definition

export class ImageOpBlitHal extends ImageOpHal<Definition, ImageOpBlitHalData> {
    constructor(ctx: ImageOpContextHal, parameters: ImageOpHalParameters<Definition>) {
        super(ctx)
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, ImageOpBlitHalData>): ImageOpBlitHalData {
        return new ImageOpBlitHalData({
            ...args,
            descriptor: args.parameters.backgroundImage.descriptor,
            outputImageReuse: "reuse-if-same-descriptor",
            updateComplexity: UPDATE_COMPLEXITY_MID,
        })
    }
}

class ImageOpBlitHalData extends ImageOpHalData<Definition> {
    constructor(args: CtorArgs<Definition, ImageOpBlitHalData>) {
        super(args)
        this.painter =
            args.prevData?.painter.addRef() ??
            this.ctx.createPainter(
                "compositor",
                `
            uniform int u_targetMipLevel;
            uniform vec2 u_sourceOffset;
            uniform vec2 u_sourceSize;
            uniform int u_sourceAddressMode;
            
            vec4 computeColor(ivec2 targetPixel) {
                vec2 sourcePixel = vec2(targetPixel) + u_sourceOffset;
                vec4 foregroundColor = textureLodPx0(sourcePixel + 0.5, u_targetMipLevel, u_sourceAddressMode);
                float coverageXn = clamp(sourcePixel.x + 1.0, 0.0, 1.0);
                float coverageXp = clamp(u_sourceSize.x - sourcePixel.x, 0.0, 1.0);
                float coverageYn = clamp(sourcePixel.y + 1.0, 0.0, 1.0);
                float coverageYp = clamp(u_sourceSize.y - sourcePixel.y, 0.0, 1.0);
                float coverage = coverageXn * coverageXp * coverageYn * coverageYp;
                return vec4(foregroundColor.rgb * coverage, coverage);
            }
            `,
            )
    }

    protected override dispose() {
        this.painter.release()
        super.dispose()
    }

    protected override async writeRegion(outputImage: HalImage, mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<void> {
        const {backgroundImage, foregroundImage, foregroundRegion: foregroundRegionIn, foregroundAddressMode, position, outOfBoundsHandling} = this.parameters
        backgroundImage.addRef("Blit.writeRegion - backgroundImage")
        foregroundImage.addRef("Blit.writeRegion - foregroundImage")
        let backgroundHalImage: RegionLockedHalImageRef | undefined
        let foregroundHalImage: RegionLockedHalImageRef | undefined
        try {
            const mipLevelScale = 1 / (1 << mipLevelRegion.mipLevel)
            const fullForegroundRegion = foregroundRegionIn ?? {
                x: 0,
                y: 0,
                width: foregroundImage.descriptor.width,
                height: foregroundImage.descriptor.height,
            }
            const backgroundRegion = determineBackgroundImageMipLevelRegion(mipLevelRegion)
            const foregroundRegion = determineForegroundImageMipLevelRegion(foregroundImage, foregroundRegionIn, position, mipLevelRegion)

            // update background and foreground image
            backgroundHalImage = await backgroundImage.getUpdatedHalImage(backgroundRegion, options)
            foregroundHalImage = Box2.isEmpty(foregroundRegion) ? undefined : await foregroundImage.getUpdatedHalImage(foregroundRegion, options)

            // copy the source image to the local image
            await this.ctx.copyImageData({
                targetImage: outputImage,
                sourceImage: backgroundHalImage.halImage,
                region: mipLevelRegion,
                mipLevel: mipLevelRegion.mipLevel,
            })

            if (foregroundHalImage) {
                const mipLevelPosition = {
                    x: position.x * mipLevelScale,
                    y: position.y * mipLevelScale,
                }
                const mipLevelFloorPosition = {
                    x: Math.floor(mipLevelPosition.x),
                    y: Math.floor(mipLevelPosition.y),
                }
                const subPixelOffset = {
                    x: mipLevelPosition.x - mipLevelFloorPosition.x,
                    y: mipLevelPosition.y - mipLevelFloorPosition.y,
                }
                const p00 = {
                    x: Math.max(mipLevelRegion.x, mipLevelFloorPosition.x),
                    y: Math.max(mipLevelRegion.y, mipLevelFloorPosition.y),
                }
                const p11 = {
                    x: Math.min(mipLevelRegion.x + mipLevelRegion.width, Math.ceil((position.x + fullForegroundRegion.width) * mipLevelScale)),
                    y: Math.min(mipLevelRegion.y + mipLevelRegion.height, Math.ceil((position.y + fullForegroundRegion.height) * mipLevelScale)),
                }
                const regionToDraw = Box2.fromMinMax(p00, p11)
                if (regionToDraw.isEmpty()) {
                    ImageOpBlitHalData.logger?.warn("Region to draw is empty, nothing to blit (even though this should have been caught earlier)")
                } else {
                    // TODO is this correct ? it should be in sync with sub-pixel behaviour of extract-region
                    // const sourceOffset = {
                    //     x: -position.x * mipLevelScale + subPixelOffset.x,
                    //     y: -position.y * mipLevelScale + subPixelOffset.y,
                    // }
                    const sourceOffset = {
                        x: -mipLevelFloorPosition.x - subPixelOffset.x,
                        y: -mipLevelFloorPosition.y - subPixelOffset.y,
                    }
                    const regionsToDraw = getRegionsToDraw(outOfBoundsHandling, backgroundImage, regionToDraw, mipLevelRegion.mipLevel)
                    for (const currRegion of regionsToDraw) {
                        this.painter.ref.paint({
                            target: outputImage,
                            sourceImages: foregroundHalImage.halImage.createView({
                                type: "sub-image",
                                region: fullForegroundRegion,
                            }),
                            parameters: {
                                u_targetMipLevel: {type: "int", value: mipLevelRegion.mipLevel},
                                u_sourceOffset: {type: "float2", value: sourceOffset},
                                u_sourceSize: {
                                    type: "float2",
                                    value: {x: fullForegroundRegion.width * mipLevelScale, y: fullForegroundRegion.height * mipLevelScale},
                                    isOptional: true, //TODO remove this
                                },
                                u_sourceAddressMode: {type: "int", value: getHalAddressMode(foregroundAddressMode)},
                            },
                            options: {
                                targetMipLevel: mipLevelRegion.mipLevel,
                                targetRegion: currRegion,
                                blendMode: "over",
                            },
                        })
                    }
                }
            }
        } finally {
            backgroundHalImage?.release()
            foregroundHalImage?.release()
            backgroundImage.release("Blit.writeRegion - backgroundImage")
            foregroundImage.release("Blit.writeRegion - foregroundImage")
        }
    }

    protected override onSourceRegionsInvalidated(sourceImage: AnyImageOpHalData, sourceRegions: Regions) {
        const targetRegion = determineTargetImageRegion(this.parameters, sourceImage, sourceRegions)
        this.invalidateRegions(targetRegion)
    }

    private painter: HalPainterImageCompositorRef
}
